import { v4 } from "uuid";
import { UAParser, type IResult } from "ua-parser-js";

import { getCookie } from "util/cookie";
import {
  MARKETING_SESSION_ID_COOKIE_KEY,
  MARKETING_SESSION_DATA_COOKIE_KEY,
} from "util/marketing_session";
import { getDeviceId } from "util/device";
import Env from "config/environment";
import { readMarketingFlowType } from "common/analytics/signer_flow/util";

import { isMobileDevice } from "./support";
import { browserReport as browserReportImported } from "./browser_report";

type BrowserReport = IResult & {
  userAgent: string;
  marketingSession: {
    id: null | undefined | string;
    data: null | undefined | string;
  };
  flow_type: null | undefined | string;
};

export type MarketingSessionData = {
  uca?: null | string;
  uco?: null | string;
  ui?: null | string;
  um?: null | string;
  usrc?: null | string;
  ut?: null | string;
};
type UtmKey = keyof MarketingSessionData;
type UtmParameter = "content" | "id" | "medium" | "name" | "source" | "term";

// We create a "application instance" or tab ID for logs so we can see which browser tabs generated events
const BROWSER_TAB_ID = v4();
const { releaseName } = Env;

export function browserReport(): BrowserReport {
  const uap = new UAParser();
  const result = uap.getResult();
  const report: BrowserReport = {
    ...result,
    userAgent: result.ua,
    marketingSession: {
      id: null,
      data: null,
    },
    flow_type: null,
  };

  // UAParser doesn't recognize 'desktop' as a device type.
  // For our reporting purposes, we can consider 'undefined' as 'desktop'.
  report.device.type = report.device.type || "desktop";

  const flowType = readMarketingFlowType();
  report.flow_type = flowType;

  // marketing session id and data
  report.marketingSession.id = getCookie(MARKETING_SESSION_ID_COOKIE_KEY);
  report.marketingSession.data = getCookie(MARKETING_SESSION_DATA_COOKIE_KEY);

  return report;
}

/** For request headers */
export function browserReportHeaders(): Record<string, string | null | undefined> {
  const { os, marketingSession } = browserReport();
  return {
    "X-Notarize-Device-ID": getDeviceId(),
    "X-Notarize-OS": os.name,
    "X-Notarize-OSVersion": os.version,
    "X-Notarize-Marketing-Session-ID": marketingSession.id,
    "X-Notarize-Marketing-Session-Data": marketingSession.data,
    "X-Notarize-Client-Version": releaseName,
    "X-Notarize-Platform": isMobileDevice() ? "MOBILE_WEB" : "WEB",
  };
}

export function browserReportProperties() {
  const { browser, os, device, marketingSession, flow_type, engine } = browserReport();
  return {
    browser: browser.name,
    browserVersion: browser.version,
    browserTabId: BROWSER_TAB_ID,
    deviceID: getDeviceId(),
    deviceType: device.type,
    deviceVendor: device.vendor,
    deviceModel: device.model,
    os: os.name,
    osVersion: os.version,
    engineName: engine.name,
    engineVersion: engine.version,
    marketingSessionID: marketingSession.id,
    marketingSessionData: marketingSession.data,
    flow_type,
    version: releaseName,
  };
}

// The Marketing session data cookie contains more than just the UTM parameters,
// so we want to filter non-UTM and blank values out
function formatSessionData(data: MarketingSessionData) {
  const utmMap: Record<UtmKey, UtmParameter> = {
    uco: "content",
    ui: "id",
    um: "medium",
    uca: "name",
    usrc: "source",
    ut: "term",
  };

  const campaign = {} as Record<UtmParameter, string>;
  Object.keys(utmMap).forEach((mapKey) => {
    const key = mapKey as UtmKey;
    if (data[key]) {
      campaign[utmMap[key]] = data[key];
    }
  });

  if (Object.keys(campaign).length) {
    return { campaign };
  }

  return {};
}

export function browserReportContext() {
  const { marketingSession } = browserReportImported();
  let marketingSessionData: MarketingSessionData = {};

  try {
    marketingSessionData = JSON.parse(marketingSession.data || "{}") || {};
  } catch {
    marketingSessionData = {};
  }

  return formatSessionData(marketingSessionData);
}
